<template>
  <v-container>
    <v-alert v-if="timedOut" max-width="600" type="warning" class="mx-auto">
      {{ $t('view.something_went_wrong_15_second') }}
    </v-alert>

    <v-card v-if="isReward" flat>
      <v-card-text class="text-h6">
        <v-row align="center" justify="space-around">
          <v-col cols="7" md="3">
            {{ $t('view.tokens_to_burn_for') }} {{ eventName || nonce }}
            <!-- when field is not readonly, set type=number   -->
            <v-text-field
              class="text-h5"
              :loading="loading"
              :value="tokens"
              readonly
              :hint="lastEarnedHint"
              persistent-hint
            >
              <template v-slot:progress>
                <v-progress-linear
                  indeterminate
                  :color="yellow"
                  absolute
                  height="7"
                ></v-progress-linear> </template
            ></v-text-field>
          </v-col>
          <v-col cols="5" md="9">
            <v-row dense>
              <v-col>
                <v-btn
                  color="primary"
                  :disabled="disableBurnButtons"
                  @click="burnAll"
                  block
                  rounded
                  >{{ $t('view.burn_all') }}</v-btn
                >
              </v-col>

              <v-col>
                <v-btn
                  color="primary"
                  :disabled="disableBurnButtons"
                  @click="burnSome"
                  block
                  rounded
                  >{{ $t('view.burn_some') }}</v-btn
                >
              </v-col>

              <v-col>
                <!-- <v-btn color="accent" block rounded @click="register"
                  >{{ $t('view.register') }}</v-btn
                > -->
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
      <div v-html="rewardsViewHtml"></div>
    </v-card>

    <pre v-if="showDebug">
Debug:

</pre
    >

    <confirm-dialog ref="confirm">
      <v-form ref="form">
        <v-row justify="space-between">
          <v-col>
            <v-text-field
              v-model="approvedTokens"
              :label="$t('view.tokens_to_burn')"
              readonly
              type="number"
              :rules="[
                () => approvedTokens >= 0 || 'Enter a positive number, please.',
              ]"
            />
          </v-col>
          <v-col>
            <v-row dense>
              <v-col>
                <v-btn color="accent" block @click="incr">
                  <v-icon left> mdi-plus </v-icon>{{ $t('view.burn_more') }}</v-btn
                >
              </v-col>

              <v-col>
                <v-btn color="accent" block @click="decr"
                  ><v-icon left> mdi-minus</v-icon>{{ $t('view.burn_less') }}</v-btn
                >
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
    </confirm-dialog>

    <confirm-dialog ref="confirmEventEarnOrBurn"> </confirm-dialog>

    <toast :options="toastOptions"></toast>
  </v-container>
</template>

<script>
const ConfirmDialog = () => import('../components/ConfirmDialog.vue');
const Toast = () => import('@/components/toast.vue');
const { CRDs } = require('../../srv/redis/constants');

import {
  emitFromClient,
  isEmpty,
  lookAtMeColor,
  pj,
  urlBuilder,
} from '../../srv/utils/helpers';
import { TARGET_NAMES } from '../../srv/ops/rewards';
import { TARGET_NAMES as TARGET_NAMES_CAMPAIGNS } from '../../srv/ops/campaigns';

export default {
  props: {
    nonce: String,
    eventName: String,
    earn: String,
  },
  components: {
    ConfirmDialog,
    Toast,
  },

  computed: {
    lastEarnedHint() {
      return this.loading
        ? this.$i18n.t('view.counting_earnings')
        : `${this.$i18n.t('view.last_earned')} ${this.lastEarned}`;
    },

    debug() {
      return false;
    },
    showDebug() {
      return this.debug;
    },

    mappedEvents() {
      const array = this.maps?.events ? [...this.maps.events] : [];
      return array;
    },

    connectionType() {
      return this.$route.meta.connectionType;
    },

    targetNames() {
      return this.isReward ? TARGET_NAMES : TARGET_NAMES_CAMPAIGNS;
    },

    targetName() {
      return this.connectionType === 'Reward'
        ? this.targetNames.tokens
        : this.targetNames.eventTokens;
    },

    isReward() {
      return this.connectionType === 'Reward';
    },
    isContact() {
      return this.connectionType === 'Contact';
    },

    role() {
      return this.isReward ? 'rewards' : 'campaigns';
    },

    context() {
      //return `${this.$i18n.t('view.connectiontype', {'connectionType': this.$i18n.t(`view.connectionType.${this.connectionType}`)})} for: ${this.nonce} `;
			//context is too long to be displayed on the sysbar, return empty string for now until the info is required and suitable display location determined
			return '';
    },
    earnBannerUrl() {
      return this.currentEvent?.bannerUrl;
    },
    burnBannerUrl() {
      return this.currentEvent?.burnBannerUrl;
    },

    url() {
      return `${this.cid}`;
    },

    cid() {
      const cid = this.query.cid ?? this.$route.query.cid ?? this.$pk;
      console.log('cid :>> ', cid);
      return cid;
    },

    source() {
      return this.$route.query.pk;
    },

    earning() {
      return this.earn && this.earn === 'earn';
    },

    burning() {
      return this.earn && this.earn === 'burn';
    },

    campaignUrl() {
      return `${this.query?.url}?country=${this.$country}&nonce=${this.nonce}&cid=${this.$pk}`;
    },

    tokenCt() {
      return this.loading ? 'counting...' : this.tokens;
    },

    disableBurnButtons() {
      return this.loading || !this.tokens;
    },

    path() {
      if (this.isReward) {
        return `/view/rewards/${this.nonce}`;
      }
      else if (this.isContact) {
        return 'view/contacts/'
      }
      return `/view/events/${this.nonce}/${this.eventName}`;
    },
  },
  data() {
    return {
      lastEarned: '',
      eventTokens: null,
      currentEvent: null,
      // passed to Model
      params: {
        cid: '',
        nonce: '',
        tokens: 0,
      },
      query: {},
      loading: true,
      approvedTokens: 0,
      rewardsViewHtml: '',
      tokens: '',
      client: this.$socket.client,

      maps: { events: null, rewards: null, tokens: null },

      earnRewardUrl: '',
      burnRewardUrl: '',

      toastOptions: {
        showToast: false,
        text: '',
        button: '',
        timeout: -1,
        centered: true,
        other: { top: false },

        timeOut: null,
        timedOut: false, // timeOut timedOut
      },
    };
  },

  methods: {
    key(keyType) {
      // example key for Event :>  tqr:lo:chrome:events:ChromeEvent
      // example key for Tokens :> tqr:lo:chrome:ChromeEvent:tokens:1671044758625-0
      const match = {
        event: `${this.$preambleBase}:${this.nonce}:events:${this.eventName}`,
        tokens: `${this.$preambleBase}:${this.nonce}:${this.eventName}:tokens:${this.$pk}`,
      };
      return match[keyType];
    },

    ready() {
      this.loading = false;
      clearTimeout(this.timeOut);
      this.$emit('statusChange', 'Ready');
    },

    register() {
      const country = this.$preambleBase.substring(4, 6);
      this.toast(this.$i18n.t('view.going_to_token_registration'));
      const url =
        window.location.pathname === '/portal'
          ? `pid?p=${this.$pk}`
          : `cid?c=${this.$pk}`;
      window.open(
        `https://sg.tqrtoken.com/save-${url}&country=${country}`, //hardcode to sg for now till the next production level
        //`https://${country}.tqrtoken.com/save-${url}&country=${country}`,
        '_self',
        'noopener noreferrer'
      );
    },

    toast(msg, button = 'Ok', timeout = 4000, fnc = null) {
      this.toastOptions.text = msg;
      this.toastOptions.button = button;
      this.toastOptions.timeout = timeout;
      this.toastOptions.fnc = fnc;
      this.toastOptions.showToast = true;
    },

    incr() {
      this.approvedTokens =
        this.approvedTokens < this.tokens
          ? this.approvedTokens + 1
          : this.approvedTokens;
    },

    decr() {
      this.approvedTokens =
        this.approvedTokens === 0 ? 0 : this.approvedTokens - 1;
    },

    redirect(side = 'earn') {
      const nonce = this.nonce;
      console.log('redirect().nonce :>> ', nonce);

      const redirectEvent = () => {
        console.log('redirectEvent() this.tokens :>> ', this.tokens);
        console.log('redirectEvent() this.eventTokens :>> ', this.eventTokens);
        // when earning, there are tokens in this.tokens
        // after burning, this.tokens is 0, made so by this.eventTokens
        const tokens = this.tokens || this.eventTokens;
        console.log('redirectEvent() tokens :>> ', tokens);

        const options = {
          earn: this.currentEvent.redirect,
          burn: this.currentEvent.burnRedirectUrl,
        };
        const redirect = options[side];
        console.log('redirect :>> ', redirect);

        if (redirect) {
          // got to foreign site
          const url = urlBuilder({
            route: [],
            search: [
              ['country', this.$country],
              ['nonce', this.nonce],
              ['eventName', this.currentEvent.eventName], // TODO confirm this fixes Ruen's issue 1/10
              ['cid', this.$pk],
              ['tokens', tokens],
              ['defer', this.currentEvent.defer],
            ],
            origin: redirect,
          });
          console.log('url :>> ', url);
          window.open(url, '_self', 'noopener noreferrer');
          return true;
        }
      };

      const rewardRedirect = () => {
        console.log('rewardRedirect().side :>> ', side);

        const preamble = this.$preambleBase;
        const targetName = CRDs.getJson;

        const context = {
          preamble,
          nonce,
        };
        console.warn('context :>> ', pj(context));
        const role = 'rewards';
        console.warn('Getting redirect from Redis...');

        // get redirect urls from Redis.JSON
        emitFromClient(this.client, role, context, targetName, (resolved) => {
          console.log('resolved :>> ', pj(resolved));
          if (isEmpty(resolved)) {
            return;
          }
          const options = {
            earn: resolved.json.earnRewardUrl,
            burn: resolved.json.burnRewardUrl,
          };
          const redirect = options[side];
          if (isEmpty(redirect)) {
            return;
          }

          // got to foreign site
          const url = urlBuilder({
            route: [],
            search: [
              ['country', this.$country],
              ['nonce', this.nonce],
              ['cid', this.cid],
              ['tokens', this.tokens],
            ],
            origin: redirect,
          });
          console.log('url :>> ', url);
          window.open(url, '_self', 'noopener noreferrer');
          return true;
        });
      };
      if (this.isReward) {
        rewardRedirect();
      } else {
        redirectEvent();
      }
    },

    addTokens(t) {
      // goal: SCAN 0 MATCH tqr:lo:*:tokens:1659059618220-0 count 2000
      const nonce = this.nonce;
      const pk = this.$route.query.pk ?? '*';

      const preamble = this.$preambleBase;
      const pid = pk;
      const cid = this.cid;
      const tokens = t;
      const redirect = '';
      const eventName = this.eventName;

      // burning tokens "earns" a neg tokens
      const targetName = this.targetNames.earn;

      const context = {
        preamble,
        nonce,
        pid,
        cid,
        tokens,
        eventName,
        redirect,
      };

      const role = this.role;
      console.warn('addTokens().context :>> ', pj(context));
      console.log(' ');
      return new Promise((resolve) => {
        emitFromClient(this.client, role, context, targetName, (id) =>
          resolve({ id, nonce, t })
        );
      });
    },

    // update the UI
    congratulate({ id, nonce }, tokens) {
      this.tokens = this.tokens - tokens;
      if (this.$router.currentRoute.path !== this.path) {
        this.$router.push(this.path);
      }
      const midSentence = this.isReward
        ? nonce
        : `${nonce} ${this.eventName} ${this.$i18n.t('view.event')}`;
      const message = `${this.$i18n.t('view.congratulations_you_burned', {tokens: tokens, midSentence: midSentence, plural: +tokens === 1 ? '' : 's', id: id})} 
				${this.$i18n.t('view.total_equals', {tokens: tokens})}`;
      const args = {
        title: this.$i18n.t('view.token_burn'),
        bannerUrl: this.burnBannerUrl,
        message,
        options: { color: 'primary', width: 400 },
      };

      this.$refs.confirmEventEarnOrBurn.open(args).then((ok) => {
        if (ok) {
          this.redirect('burn');
        }
      });
    },

    // first button pressed
    burnAllTokens(proceed) {
      if (!this.tokens || !proceed) {
        this.redirect('burn');
        return;
      }
      if (proceed) {
        this.addTokens(-this.tokens).then((result) =>
          this.congratulate(result, this.tokens)
        );
      }
    },

    burnAll() {
      console.log('\n#################################');
      console.log('this.eventTokens :>> ', this.eventTokens);
      console.log('this.tokens :>> ', this.tokens);
      this.tokens = this.eventTokens ? this.eventTokens : this.tokens;
      console.log('this.tokens :>> ', this.tokens);
      console.log('#################################');

      const rewardPhrase = this.isReward
        ? this.$i18n.t('view.show_this_to_the_provider')
        : '';
      const message = this.tokens
        ? `${this.$i18n.t('view.you_are_about_to_burn', {thisTokens: this.tokens})} ${rewardPhrase}`
        : this.$i18n.t('view.you_have_no_tokens');

      //const burnPasscode = this.isReward
      //  ? ''
      //  : this.currentEvent.burnPasscode;

      const confirmDialog = (burnPasscode) => {
        this.$refs.confirm
          .open({
            title: this.$i18n.t('view.burn_tokens'),
            bannerUrl: this.burnBannerUrl,
            message,
            defaultVal: '',
            options: {
              color: 'primary',
              width: 400,
              noconfirm: false,
            },
            dialogType: 'passcode',
            passcode: this.tokens ? burnPasscode : '',
          })
          .then((proceed) => this.burnAllTokens(proceed));
      }

      if(this.isReward) {  
        this.getData('burnPasscode').then((burnPasscode) => {
          confirmDialog(burnPasscode);
        });
      } else {
        confirmDialog(this.currentEvent.burnPasscode);
      }  
    },

    // second button pressed
    openBurnConfirm(availableTokens) {
      const rewardPhrase = this.isReward
        ? this.$i18n.t('view.show_this_to_the_provider')
        : '';
      
      return new Promise((resolve) => {
        const confirmDialog = (burnPasscode) => {
          this.$refs.confirm
            .open({
              title: this.$i18n.t('view.burn_tokens'),
              message: availableTokens
                ? `${this.$i18n.t('view.you_have_available_tokens', {availableTokens: availableTokens})} ${rewardPhrase}`
                : this.$i18n.t('view.we_are_sorry'),
              prompt: availableTokens,
              defaultVal: '',
              options: {
                color: 'primary',
                width: 400,
                noconfirm: false,
              },
              dialogType: 'passcode',
              passcode: this.tokens ? burnPasscode : '',
            })
            .then((proceed) => {
              resolve(proceed);
            });
        }
        
        if(this.isReward) {  
          this.getData('burnPasscode').then((burnPasscode) => {
            confirmDialog(burnPasscode);
          });
        } else {
          return confirmDialog(this.currentEvent.burnPasscode);
        }  
      })
    },

    burnSome() {
      if (!this.tokens) {
        const args = {
          title: this.$i18n.t('view.burn_reward_tokens'),
          message: this.$i18n.t('view.sorry_you_cant_do_that'),
          options: { color: 'secondary', width: 400 },
        };

        this.$refs.confirmEventEarnOrBurn
          .open(args)
          .then(() => this.redirect('burn'));
      } else {
        const tokens = this.tokens;

        this.openBurnConfirm(tokens).then((proceed) => {
          if (!proceed || tokens === 0 || this.approvedTokens === 0) {
            this.redirect('burn');
            return;
          }

          this.addTokens(-this.approvedTokens).then((result) =>
            this.congratulate(result, this.approvedTokens)
          );
        });
      }
    },

    getJson() {
      const nonce = this.nonce;
      const preamble = this.$preambleBase;

      const targetName = 'getJson';

      const context = {
        preamble,
        nonce,
      };
      console.warn('context :>> ', pj(context));
      const role = 'rewards';
      return new Promise((resolve) => {
        emitFromClient(this.client, role, context, targetName, (json) =>
          resolve(json)
        );
      });
    },

    scanEvents(nonce) {
      // Events example:
      //  tqr:xx:nrokX@camp:events:*
      //  [0] [1][2]        [3]    [4]
      const preamble = this.$preambleBase;
      const pid = this.$route.query.pk ?? '';
      const cid = this.$pk;

      const targetName = 'events';

      const context = {
        preamble,
        nonce,
        pid,
        cid,
      };
      const role = 'campaigns';
      console.log('scanEvents()');
      console.warn(role);
      console.warn(pj(context));
      console.warn(targetName);
      console.log(' ');

      return new Promise((resolve) => {
        emitFromClient(this.client, role, context, targetName, (data) =>
          resolve({ nonce, data })
        );
      });
    },
    scanTokens() {
      // goal: SCAN 0 MATCH tqr:lo:*:tokens:1659059618220-0 count 2000
      const nonce = '*';
      const pk = this.$route.query.pk ?? '*';

      const preamble = this.$preambleBase;
      const pid = pk;
      const cid = this.$pk;

      const tokens = '';
      const redirect = this.r ?? '';

      const context = {
        preamble,
        nonce,
        pid,
        cid,
        tokens,
        redirect,
      };

      const role = this.role;

      const targetName = this.targetName;
      console.log('scanTokens()');
      console.log('this.connectionType :>> ', this.connectionType);
      console.log('role and targetName :>> ', role, targetName);
      console.log('context :>> ', context);

      return new Promise((resolve) => {
        emitFromClient(this.client, role, context, targetName, (tokensMap) =>
          resolve(tokensMap)
        );
      });
    },

    // see setData() in Model
    getData(hashField) {
      const nonce = this.nonce;
      const preamble = this.$preambleBase;

      const targetName = CRDs.getHash;

      const context = {
        preamble,
        nonce,
        hashName: 'data',
        hashField,
      };
      console.warn('context :>> ', pj(context));
      const role = 'rewards';
      return new Promise((resolve) => {
        emitFromClient(this.client, role, context, targetName, (data) =>
          resolve(data)
        );
      });
    },

    // setData(hashField, hashData) {
    //   const nonce = this.nonce;
    //   const preamble = this.$preambleBase;

    //   const targetName = CRDs.setHash;

    //   const context = {
    //     preamble,
    //     nonce,
    //     hashField,
    //     hashData,
    //   };
    //   console.warn('context :>> ', pj(context));
    //   const role = 'rewards';
    //   emitFromClient(this.client, role, context, targetName, ({ KEY }) =>
    //     this.toast(`Updated ${KEY} hash table`, 'ok', -1)
    //   );
    // },

    available(tokensMap) {
      // tqr:lo:chrome:ChromeEvent:tokens:1671044758625-0
      const key = this.isReward
        ? `${this.$preambleBase}:${this.nonce}:tokens:${this.$pk}`
        : `${this.$preambleBase}:${this.nonce}:${this.eventName}:tokens:${this.$pk}`;
      console.info('available().key :>> ');
      console.info(key);
      this.maps.tokens = new Map(tokensMap);
      const node = this.maps.tokens.get(key);

      if (isEmpty(node)) {
        this.tokens = 0;
        this.ready();

        return;
      }
      // add pos and neg tokens for total
      // TODO account for NaN in tokens' value
      const countTokens = () =>
        node.reduce((a, c) => {
          // make sure blank tokens don't return NaN
          a += parseInt(+c.tokens);
          return a;
        }, 0);
      const x = countTokens();
      this.tokens = x;
      this.lastEarned = new Date(+node[0].id.slice(0, 13));
      console.log('Leaving available()');
      this.ready();
    },

    burnEventTokens() {
      if (this.isReward) {
        return;
      }

      this.getEventTokens().then((token) => {
        this.eventTokens = 0;
        if (token) {
          this.eventTokens = token.reduce((a, c) => {
            a += +c.tokens;
            return a;
          }, 0);
          console.warn(this.eventTokens);
          console.log(' ');
        }
        this.burnAll();
      });
    },

    getEventTokens() {
      // goal: XRANGE tqr:lo:chrome@Test:Test Event:tokens:1673304964492-0 - +
      const preamble = this.$preambleBase;
      const nonce = this.nonce;
      const eventName = this.eventName;
      const cid = this.cid;

      const targetName = this.targetNames.readEventTokens;

      const context = {
        preamble,
        nonce,
        eventName,
        cid,
      };

      const role = this.role;
      console.warn('getEventTokens().context :>> ', pj(context));
      console.log(' ');
      return new Promise((resolve) => {
        emitFromClient(this.client, role, context, targetName, (tokens) =>
          resolve(tokens)
        );
      });
    },

    getAllEventTokens() {
      // goal: XRANGE tqr:lo:chrome@Test:Test Event:tokens:* - +
      const preamble = this.$preambleBase;
      const nonce = this.nonce;
      const eventName = this.eventName;
      const cid = '*';

      console.log(this);
      const targetName = this.targetNames.eventTokens;

      const context = {
        preamble,
        nonce,
        eventName,
        cid,
      };

      const role = this.role;
      console.warn('getAllEventTokens().context :>> ', pj(context));
      console.log(' ');
      return new Promise((resolve) => {
        emitFromClient(this.client, role, context, targetName, (tokens) => {
          console.log(tokens);
          resolve(tokens); }
        );
      });
    },

    /*
    Old Code
    Fm Producer:  https://zz.tqrtoken.net/event/ruen@campaigntest?e=earnalltokens&g=https%3A%2F%2Ftqrtoken.com%2Fcampaign-earn%2F
    Fm View:      https://tqrtoken.com/campaign-earn/?c=1670572876367-0&n=ruen%40campaigntest
    Producer breakdown:
    href:           https://zz.tqrtoken.net/event/ruen@campaigntest
    query:          ?e=earnalltokens
                    &g=https%3A%2F%2Ftqrtoken.com%2Fcampaign-earn%2F
    Opens:        https://tqrtoken.com/campaign-earn/?c=1670572876367-0&n=ruen%40campaigntest

    New Code
    campaignUrl:  https://tqrtoken.com/campaign-earn
    campaigns:    http://localhost:3333/view/events/ruen@campaigntest/earnalltokens/
                  &g=https%3A%2F%2Ftqrtoken.com%2Fcampaign-earn%2F
    consumer:     https://tqrtoken.com/campaign-earn/?c=1669538990183-0&n=ruen%40campaigntest

    Now:          https://tqrtoken.com/campaign-earn?nonce=dbg%40url&cid=1673304964492-0
    Will be:      http://localhost:3333/view/events/dbg@url/testUrl/
                  &url=https%3A%2F%2Ftqrtoken.com%2Fcampaign-earn%2F

    produces:     https://tqrtoken.com/campaign-earn/?cid=1670572876367-0&nonce=ruen%40campaigntest
    */
    openCampaignUrl() {
      window.open(this.campaignUrl, '_self', 'noopener noreferrer');
    },
  },

  watch: {
    tokens(n, o) {
      console.warn('tokens new', n);
      console.warn('tokens old', o);
      console.log(' ');
    },
    loading(n) {
      this.$emit('statusChange', n ? 'loading' : 'loaded');

      const increment = ({ id, nonce, t }) => {
        console.log(lookAtMeColor('\naddTokens() returns: ', id, nonce, t));

        // +t ensures a Number
        this.tokens = this.tokens + (id ? +t : 0);
        const datePart = id.slice(0, 13);
        console.log(datePart);
        console.log(+datePart);
        this.lastEarned = new Date(+datePart);

        const midSentence = this.isReward
          ? nonce
          : `${nonce} ${this.eventName} ${this.$i18n.t('view.event')}`;
        const message = `${this.$i18n.t('view.well_done_you_just_earned', {t: t, midSentence: midSentence, plural: +t === 1 ? '' : 's', id: id})} 
					${this.$i18n.t('view.total_equals', {tokens: this.tokens})}`;
        const args = {
          title: this.$i18n.t('view.earn_tokens'),
          bannerUrl: this.earnBannerUrl,
          message,
          options: { color: 'primary', width: 400 },
        };

        this.$router.push(this.path);

        this.$refs.confirmEventEarnOrBurn.open(args).then((ok) => {
          if (ok) {
            // TODO remove earn redirect from Rewards and test function in campaigns
            this.redirect('earn');
            // window.location.reload()
          }
        });
        console.info('End of Earning Tokens\n');
      };
      const cacheEvents = ({ nonce, data }) => {
        // this.logDebugLog('scanEvents', { nonce, data }, false);
        console.info('scanEvents() nonces match:', this.nonce === nonce);
        this.maps.events = new Map(data);
        // example key for Event :> tqr:lo:chrome:events:ChromeEvent
        const key = this.key('event');
        console.info('scanEvents().key', key);
        const value = this.maps.events.get(key)[0];
        console.info('event :>> ', pj(value));
        console.info('t :>> ', value.tokens);
        this.currentEvent = value;

        return value.tokens;
      };

      const earnEventTokens = (tokens) => {
        // TODO this should be in Model
        // if the event already has a tokens record, redirect
        // first we get the tokens for this cid
        
        this.getEventTokens().then((t) => {
          console.log('this.getEventTokens() tokens :>> ', pj(t));
          console.log(' ');

          if (t) {
            const args = {
              title: this.$i18n.t('view.earn_event_tokens_warning'),
              bannerUrl: this.earnBannerUrl,
              message: this.$i18n.t('view.sorry_you_already_earned', {eventName: this.currentEvent.eventName}),
              options: { color: 'secondary', width: 400 },
            };

            this.$refs.confirmEventEarnOrBurn
              .open(args)
              .then(() => this.redirect('earn'));
          } else {
            this.addTokens(tokens).then(({ id, nonce, t }) => {
              console.log(
                lookAtMeColor(
                  'getEventTokens().addTokens() returns: ',
                  id,
                  nonce,
                  t
                )
              );
              increment({ id, nonce, t });
            });
          }
        });
      };

      const isDeferEarn = (tokens) => {
        if (this.currentEvent.defer !== "true") {
          earnEventTokens(tokens);
        } else {
          // just redirect
          this.tokens = tokens;
          this.redirect('earn');
        }
      }

      const earnTokens = (tokens) => {
        const daysPassed = (since) => {
          const now = new Date();
          now.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison

          const startDate = new Date(since); // Clone and set time to midnight
          startDate.setHours(0, 0, 0, 0);

          const elapsedDays = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
          return elapsedDays;
        }
        const earnAddTokens = (proceed) => {
          // to earn tokens, add zero tokens here so server will add tokenValue from reward data hash
          if (proceed) {
            this.getData('tokens')
              .then((t) => this.addTokens(t))
              .then(({ id, nonce, t }) => {
                console.log(
                  lookAtMeColor(
                    'earnTokens().addTokens() returns: ',
                    id,
                    nonce,
                    t
                  )
                );
                increment({ id, nonce, t });
              });
          }
        }
        const confirmDialog = (earnPasscode) => {
          this.$refs.confirm
            .open({
              title: this.$i18n.t('view.earn_tokens'),
              bannerUrl: this.earnBannerUrl,
              defaultVal: '',
              options: {
                color: 'primary',
                width: 400,
                noconfirm: false,
              },
              dialogType: 'passcode',
              passcode: this.tokens ? earnPasscode : '',
            })
            .then((proceed) => earnAddTokens(proceed));
        }
        if (this.earning) {
          console.info('\nEarning Tokens');
          if (this.isReward) {
            this.getData('earnInterval').then((i) => {
            const daysLeft = i - daysPassed(this.lastEarned);
              if (daysLeft > 0) {
                //this.toast(daysLeft + ' more day(s) to earn your next token');
                this.$refs.confirm
                .open({
                  title: this.$i18n.t('view.earn_tokens'),
                  bannerUrl: this.earnBannerUrl,
                  message: daysLeft + ' more day(s) to earn your next token',
                  options: {
                    color: 'primary',
                    width: 400,
                    noconfirm: true,
                  },
                })
                .then(() => this.$router.push(this.path));
              } else {
                this.getData('earnPasscode').then((earnPasscode) => {
                  if (earnPasscode) {
                    confirmDialog(earnPasscode);
                  } else {
                    earnAddTokens(true);
                  }
                });  
              }
            });
          } else {
            //Check if total earn limit is reached
            if (this.currentEvent.totalEarnLimit !== "0") {
              this.getAllEventTokens().then((t) => {
                console.log('this.getAllEventTokens() tokens :>> ', pj(t));
                console.log(' ');
                if (t) {
                  const earnedTokens = t.reduce((sum, entry) => {
                    const objects = entry[1];
                    return sum + objects.reduce((tokenSum, obj) => tokenSum + parseInt(obj.tokens), 0);
                  }, 0);
                  console.log(earnedTokens);
                  if (earnedTokens < this.currentEvent.totalEarnLimit) {
                    /*if (this.currentEvent.defer !== "true") {
                      earnEventTokens(tokens);
                    } else {
                      this.tokens = this.currentEvent.tokens;
                      this.redirect('earn');
                    }*/
                    isDeferEarn(tokens);
                  } else {
                    const args = {
                      title: this.$i18n.t('view.earn_event_tokens_warning'),
                      bannerUrl: this.earnBannerUrl,
                      message: this.$i18n.t('view.sorry_total_earn_limit_reached', {eventName: this.currentEvent.eventName}),
                      options: { color: 'secondary', width: 400 },
                    };

                    this.$refs.confirmEventEarnOrBurn
                      .open(args)
                      .then(() => this.redirect('earn'));
                  }
                }
                else {
                  isDeferEarn(tokens);
                }
              });
            } else {
              /*
              // if we are to defer (delegate) the earn to the .com site
              if (this.currentEvent.defer !== "true") {
                earnEventTokens(tokens);
              } else {
                // just redirect
                this.tokens = this.currentEvent.tokens;
                this.redirect('earn');
              }
              */
              isDeferEarn(tokens);
            }
          }
        } else if (!this.isReward && this.burning) {
          this.burnEventTokens();
        }
      };

      if (this.isReward) {
        earnTokens();
      } else {
        console.log(lookAtMeColor('this.nonce :>> ', this.nonce));
        console.log(' ');
        this.scanEvents(this.nonce)
          .then(({ nonce, data }) => cacheEvents({ nonce, data }))
          .then((tokens) => earnTokens(tokens));
      }
    },
  },
  created() {},

  mounted() {
    // if no pk, let App finish it's new user process (already started)
    // then App will redirect to this page which then functions normally
    if (!this.$pk) {
      return;
    }
    // this.toast('Processing your request...');

    this.timeOut = setTimeout(() => {
      this.timedOut = true;
    }, 15000);

    this.query = this.$route.query;
    if (this.query.url) {
      this.openCampaignUrl();
      return;
    }

    this.scanTokens().then((tokensMap) => {
      this.available(tokensMap);
    });

    if (this.isReward) {
      this.getData('html').then((html) => {
        this.rewardsViewHtml = html;
      });
    }
    this.$emit('contextChanged', this.context);

    console.log('VIEW mounted');
    console.log(' ');
  },
};
</script>
